import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CartCtx = createContext(null);
const KEY = "sviin_cart_id";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const id = localStorage.getItem(KEY);
    if (!id) return;
    api.get("/cart", { params: { id } })
      .then((r) => setCart(r.data))
      .catch(() => localStorage.removeItem(KEY));
  }, []);

  const run = useCallback(async (fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setCart(data);
      if (data?.id) localStorage.setItem(KEY, data.id);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const add = useCallback(async (merchandiseId, quantity = 1) => {
    const lines = [{ merchandiseId, quantity }];
    const res = await run(() => (cart?.id ? api.post("/cart/lines", { cartId: cart.id, lines }) : api.post("/cart", { lines })));
    if (res) setOpen(true);
  }, [cart, run]);

  const update = (lineId, quantity) => run(() => api.patch("/cart/lines", { cartId: cart.id, lineId, quantity }));
  const remove = (lineId) => run(() => api.post("/cart/lines/remove", { cartId: cart.id, lineId }));

  return (
    <CartCtx.Provider value={{ cart, open, setOpen, busy, add, update, remove, count: cart?.totalQuantity ?? 0 }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
